import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEvents } from '../api';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import InFeedAd from './ads/InFeedAd';
import AD_CONFIG from './ads/AdConfig';
import { getBlogForEvent } from '../config/blogMapping';
import '../styles/events.css';
import { 
  getEventKeyFromEvent,
  getShareIdForEvent,
  buildSnapshotFromEvent,
  isFavorite as isFavoriteKey,
  toggleFavorite as toggleFavoriteForSnapshot,
  getFavoritesCount,
  reconcileWithDataset,
  getIdStrategy
} from '../utils/favorites';
import {
  trackEventDetailsClick,
  trackFavoritesAdd,
  trackFavoritesRemove,
  trackFavoritesToggleView,
} from '../utils/analytics';
import { applyEventListSeo } from '../utils/eventListSeo';
import {
  getEventDisplayTags,
  getEventTypeClassSuffix,
  eventMatchesDanceTypeFilter,
  collectDanceTypeFilterOptions,
  tagLabelToClassSuffix,
} from '../utils/eventDanceTags';
import { cityFromEvent, resolveEventLocation } from '../utils/eventLocationDisplay';
import { getEventSearchTitle } from '../utils/eventSearchThemes';
import {
  DATE_RANGE_OPTIONS,
  getDateRangeFromSearch,
  getPresetDateRange,
  isDateInRange,
  updateDateRangeSearch,
} from '../utils/dateRanges';

function EventList() {
  const formatDanceTypeLabel = (type) => {
    if (!type || type === 'All') return 'All Events';
    if (type === 'Improvisation') return 'Contact Improvisation';
    return type;
  };

  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  
  // Hydrate the date range synchronously so fetching events cannot temporarily
  // replace a URL-filtered list with the full dataset during initial render.
  const initialDateRange = useRef(getDateRangeFromSearch(window.location.search)).current;

  // Filter states
  const [dateFilter, setDateFilter] = useState(initialDateRange.start);
  const [dateEndFilter, setDateEndFilter] = useState(initialDateRange.end);
  const [dateRangePreset, setDateRangePreset] = useState(initialDateRange.preset);
  const [locationFilter, setLocationFilter] = useState(() => {
    const lastSeg = window.location.pathname.split('/').filter(Boolean).pop()?.toLowerCase() || '';
    return {
      'san-francisco': 'San Francisco',
      'walnut-creek': 'Walnut Creek',
      'mountain-view': 'Mountain View',
    }[lastSeg] || '';
  });
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const locationDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  // Determine initial dance type filter based on URL, e.g. /app/tango -> "Tango" or ?style=tango
  const pathTypeToFilter = () => {
    // First check URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const styleParam = urlParams.get('style')?.toLowerCase() || '';
    
    // Then check pathname
    const segments = window.location.pathname.split('/').filter(Boolean);
    const lastSeg = segments[segments.length - 1]?.toLowerCase() || '';
    
    const map = {
      tango: 'Tango',
      fusion: 'Fusion',
      salsa: 'Salsa',
      bachata: 'Bachata',
      zouk: 'Brazilian Zouk',
      brazilianzouk: 'Brazilian Zouk',
      // Historical links used "contact" for Contact Improv; in v2 this category is "Improvisation"
      contact: 'Improvisation',
      'contact-improvisation': 'Improvisation',
      meditation: 'Meditation',
      yoga: 'Yoga',
      dance: 'Dance Show',
      'dance-show': 'Dance Show',
      ballet: 'Contemporary Ballet',
      'contemporary-ballet': 'Contemporary Ballet',
      ecstaticdance: 'Ecstatic Dance',
      'ecstatic-dance': 'Ecstatic Dance',
      improvisation: 'Improvisation',
      swing: 'West Coast Swing',
      wcs: 'West Coast Swing',
      'west-coast-swing': 'West Coast Swing',
      all: 'All'
    };
    
    // Prioritize query parameter over pathname
    return map[styleParam] || map[lastSeg] || 'All';
  };

  const [danceTypeFilter, setDanceTypeFilter] = useState(pathTypeToFilter());

  // Get unique locations for filter dropdown
  const locations = [...new Set(events.map(ev => cityFromEvent(ev)).filter(Boolean))]
    .filter(loc => loc && !loc.includes(':') && !loc.includes('Level'))
    .sort();

  const navigate = useNavigate();
  const routerLocation = useLocation();
  // State for Back to Top button visibility
  const [showBackToTop, setShowBackToTop] = useState(false);

  // If we navigated back from EventDetail, force scroll-to-top once.
  useEffect(() => {
    if (routerLocation.state?.scrollToTop) {
      window.scrollTo(0, 0);
      navigate(routerLocation.pathname + routerLocation.search, { replace: true, state: null });
    }
  }, [routerLocation.key, navigate, routerLocation.pathname, routerLocation.search, routerLocation.state]);

  // Ensure Events page starts at the top when (re)mounted (e.g. browser back from EventDetail).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setError(null);
        const data = await getEvents();
        // Filter out "No event here" entries and sort the remaining events
        const filteredData = data.data.filter(event => 
          event['Event Name'] && !event['Event Name'].includes('No event')
        );
        const sortedEvents = filteredData.sort((a, b) => a.Date.localeCompare(b.Date));
        setEvents(sortedEvents);
        setLoading(false);
        // Reconcile favorites with today's dataset
        const keys = new Set(sortedEvents.map(ev => getEventKeyFromEvent(ev)));
        const todayDatasetDate = dayjs().format('YYYY-MM-DD');
        reconcileWithDataset({ datasetDate: todayDatasetDate, currentEventKeys: keys });
        setFavoritesCount(getFavoritesCount());
      } catch (err) {
        setError('Failed to fetch events');
        setLoading(false);
      }
    };
  
    fetchEvents();
  }, []);

  // Read URL parameters and apply filters on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const danceType = urlParams.get('danceType');
    const location = urlParams.get('location');
    
    if (danceType) {
      setDanceTypeFilter(danceType);
    }
    if (location) {
      setLocationFilter(location);
    }
  }, []);

  // Keep date filters shareable and reload-safe.
  useEffect(() => {
    const nextSearch = updateDateRangeSearch(
      routerLocation.search,
      dateFilter,
      dateEndFilter
    );

    if (nextSearch !== routerLocation.search) {
      navigate(
        { pathname: routerLocation.pathname, search: nextSearch },
        { replace: true }
      );
    }
  }, [dateFilter, dateEndFilter, navigate, routerLocation.pathname, routerLocation.search]);

  // Show Back to Top button when scrolled beyond threshold
  useEffect(() => {
    const handleScroll = () => {
      const threshold = 400; // pixels scrolled before showing button
      setShowBackToTop(window.pageYOffset > threshold);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleShareEvent = async (event) => {
    const shareId = getShareIdForEvent(event);
    const params = new URLSearchParams();
    if (danceTypeFilter && danceTypeFilter !== 'All') {
      params.append('danceType', danceTypeFilter);
    }
    if (locationFilter) {
      params.append('location', locationFilter);
    }
    if (dateFilter) {
      params.append('dateStart', dateFilter.format('YYYY-MM-DD'));
    }
    if (dateEndFilter) {
      params.append('dateEnd', dateEndFilter.format('YYYY-MM-DD'));
    }
    const queryString = params.toString();
    const urlPath = queryString
      ? `/event/${encodeURIComponent(shareId)}?${queryString}`
      : `/event/${encodeURIComponent(shareId)}`;

    // Match v1 behavior: Web Share API first, then clipboard, then prompt.
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const url = new URL(`${base}${urlPath}`, window.location.origin).toString();
    const title = event ? getEventSearchTitle(event) : 'Event';

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch {
      // If Web Share fails, fall back to clipboard/prompt below.
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        return;
      }
    } catch {
      // Ignore and fall through to prompt.
    }

    window.prompt('Copy this event link:', url);
  };

  // Apply filters whenever filter values change
  useEffect(() => {
    let filtered = [...events];

    // Favorites filter (pure filter bubble)
    if (favoritesOnly) {
      filtered = filtered.filter(ev => isFavoriteKey(getEventKeyFromEvent(ev)));
    }

    if (dateFilter || dateEndFilter) {
      filtered = filtered.filter(event => isDateInRange(event.Date, dateFilter, dateEndFilter));
    }

    // Location filter
    if (locationFilter) {
      filtered = filtered.filter(event => {
        const city = cityFromEvent(event);
        return city && city.toLowerCase() === locationFilter.toLowerCase();
      });
    }

    // Dance type filter — prefer explicit categories, else source/keywords
    if (danceTypeFilter && danceTypeFilter !== 'All') {
      filtered = filtered.filter((event) =>
        eventMatchesDanceTypeFilter(event, danceTypeFilter)
      );
    }

    setFilteredEvents(filtered);
  }, [dateFilter, dateEndFilter, locationFilter, danceTypeFilter, favoritesOnly, events]);

  useEffect(() => {
    const hasDateQuery = Boolean(dateFilter || dateEndFilter);
    const hasFilterQuery = Boolean(window.location.search);
    applyEventListSeo(filteredEvents, {
      danceType: danceTypeFilter,
      location: locationFilter,
      indexable: !favoritesOnly && !hasDateQuery && !hasFilterQuery,
    });
  }, [filteredEvents, danceTypeFilter, locationFilter, favoritesOnly, dateFilter, dateEndFilter]);

  // Smoothly scroll to top when a filter changes (but skip initial mount)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [dateFilter, dateEndFilter, locationFilter, danceTypeFilter, favoritesOnly]);

  const eventTypes = collectDanceTypeFilterOptions(events);
  const getEventTypeClass = (event, activeFilter) =>
    getEventTypeClassSuffix(event, activeFilter);


  // Clear all filters
  const handleClearFilters = () => {
    setDateFilter(null);
    setDateEndFilter(null);
    setDateRangePreset('all');
    setLocationFilter('');
    setDanceTypeFilter('All');
    setFavoritesOnly(false);
  };

  const handleDatePresetChange = (event) => {
    const preset = event.target.value;
    setDateRangePreset(preset);
    if (preset === 'custom') return;
    const { start, end } = getPresetDateRange(preset);
    setDateFilter(start);
    setDateEndFilter(end);
  };

  // Close dropdowns when tapping/clicking outside.
  // Use pointerdown (works for mouse + touch) to make iOS behave predictably.
  useEffect(() => {
    const handlePointerDownOutside = (event) => {
      const target = event.target;

      if (locationDropdownRef.current && !locationDropdownRef.current.contains(target)) {
        setLocationDropdownOpen(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(target)) {
        setTypeDropdownOpen(false);
      }

    };

    document.addEventListener('pointerdown', handlePointerDownOutside, true);
    return () => document.removeEventListener('pointerdown', handlePointerDownOutside, true);
  }, []);

  // Toggle favorites filter
  const handleToggleFavorites = () => {
    const next = !favoritesOnly;
    setFavoritesOnly(next);
    if (next) {
      setDateFilter(null);
      setDateEndFilter(null);
      setDateRangePreset('all');
      setLocationFilter('');
      setDanceTypeFilter('All');
    }
    trackFavoritesToggleView({ enabled: next, favorites_count: getFavoritesCount() });
  };

  if (loading) {
    return (
      <div className="events-loading">
        <div className="events-loading__spinner"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="events-empty">
        <p className="events-empty__text">{error}</p>
      </div>
    );
  }

  return (
    <div className="events-page">
      {/* Hero Banner with Filters */}
      <div className="hero-banner events-hero">
        <div className="hero-banner__container events-hero__container">
          <div className="hero-banner__content">
            <h1 className="hero-banner__title">Find Your Rhythm</h1>
            <p className="hero-banner__subtitle">Upcoming dance events in the Bay Area</p>
          </div>

          {/* Filter Bar */}
          <div className="events-filter-bar" role="region" aria-label="Filters">
            <div className="events-filter-bar__row">
              {/* Date Filter */}
              <div className="events-filter-bar__date-range">
                <svg className="events-filter-bar__input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="4" width="16" height="14" rx="2" stroke="#314158" strokeWidth="1.5"/>
                  <path d="M2 8H18" stroke="#314158" strokeWidth="1.5"/>
                  <path d="M6 2V4" stroke="#314158" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M14 2V4" stroke="#314158" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <label className="sr-only" htmlFor="date-range-preset">Date range</label>
                <select id="date-range-preset" value={dateRangePreset} onChange={handleDatePresetChange}>
                  {DATE_RANGE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {dateRangePreset === 'custom' && (
                <div className="events-filter-bar__custom-range" aria-label="Custom date range">
                  <DatePicker
                    label="From"
                    value={dateFilter}
                    onChange={setDateFilter}
                    maxDate={dateEndFilter || undefined}
                    format="YYYY-MM-DD"
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <span aria-hidden="true">to</span>
                  <DatePicker
                    label="To"
                    value={dateEndFilter}
                    onChange={setDateEndFilter}
                    minDate={dateFilter || undefined}
                    format="YYYY-MM-DD"
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </div>
              )}

              {/* Location Filter */}
              <div 
                className={`events-filter-bar__dropdown ${locationDropdownOpen ? 'is-open' : ''}`}
                ref={locationDropdownRef}
                onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
              >
                <svg className="events-filter-bar__input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 18C10 18 17 12 17 7C17 3.13401 13.866 0 10 0C6.13401 0 3 3.13401 3 7C3 12 10 18 10 18Z" stroke="#314158" strokeWidth="1.5"/>
                  <circle cx="10" cy="7" r="2.5" stroke="#314158" strokeWidth="1.5"/>
                </svg>
                <span className="events-filter-bar__dropdown-text">
                  {locationFilter || 'All Locations'}
                </span>
                <svg className="events-filter-bar__dropdown-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 6L8 10L12 6" stroke="#62748E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="events-filter-bar__dropdown-menu">
                  <div 
                    className={`events-filter-bar__dropdown-option ${locationFilter === '' ? 'is-selected' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setLocationFilter(''); setLocationDropdownOpen(false); }}
                  >
                    All Locations
                  </div>
                  {locations.map((loc) => (
                    <div 
                      key={loc}
                      className={`events-filter-bar__dropdown-option ${locationFilter === loc ? 'is-selected' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setLocationFilter(loc); setLocationDropdownOpen(false); }}
                    >
                      {loc}
                    </div>
                  ))}
                </div>
              </div>

              {/* Event Type Filter */}
              <div 
                className={`events-filter-bar__dropdown ${typeDropdownOpen ? 'is-open' : ''}`}
                ref={typeDropdownRef}
                onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
              >
                <svg className="events-filter-bar__input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 5H17M5 10H15M7 15H13" stroke="#314158" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="events-filter-bar__dropdown-text">
                  {formatDanceTypeLabel(danceTypeFilter)}
                </span>
                <svg className="events-filter-bar__dropdown-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 6L8 10L12 6" stroke="#62748E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="events-filter-bar__dropdown-menu">
                  {eventTypes.map((type) => (
                    <div 
                      key={type}
                      className={`events-filter-bar__dropdown-option ${danceTypeFilter === type ? 'is-selected' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setDanceTypeFilter(type); setTypeDropdownOpen(false); }}
                    >
                      {formatDanceTypeLabel(type)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Search Button */}
              <button className="events-filter-bar__search-btn">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 19L14.65 14.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Search
              </button>

              {/* Clear Filters */}
              <button className="events-filter-bar__clear-btn" onClick={handleClearFilters}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Clear all
              </button>
            </div>

            {/* Quick Filter Chips */}
            <div className="events-filter-chips">
              <button 
                className={`events-filter-chip ${dateRangePreset === 'today' ? 'events-filter-chip--active' : ''}`}
                onClick={() => {
                  const { start, end } = getPresetDateRange('today');
                  setDateRangePreset('today');
                  setDateFilter(start);
                  setDateEndFilter(end);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 4V8L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Today
              </button>
              <button 
                className={`events-filter-chip ${favoritesOnly ? 'events-filter-chip--active' : ''}`}
                onClick={handleToggleFavorites}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 14L7.05 13.13C3.4 9.79 1 7.58 1 4.89C1 2.68 2.68 1 4.9 1C6.14 1 7.33 1.57 8 2.47C8.67 1.57 9.86 1 11.1 1C13.32 1 15 2.68 15 4.89C15 7.58 12.6 9.79 8.95 13.13L8 14Z" stroke="currentColor" strokeWidth="1.5" fill={favoritesOnly ? 'currentColor' : 'none'}/>
                </svg>
                My Favorites{favoritesCount > 0 ? ` (${favoritesCount})` : ''}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Events List */}
      <div className="events-list">
        {filteredEvents.length === 0 ? (
          <div className="events-empty">
            <div className="events-empty__inner">
              <p className="events-empty__eyebrow">No Events Found</p>
              <h2 className="events-empty__title">Empty Dance Floor</h2>
              <p className="events-empty__text">
                If this feels empty, try browsing all events or adjusting your filters
              </p>

              <div className="events-empty__actions">
                <button
                  className="events-empty__btn events-empty__btn--primary"
                  onClick={() => {
                    handleClearFilters();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19 19L14.65 14.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Browse All Events
                </button>
                <button
                  className="events-empty__btn events-empty__btn--secondary"
                  onClick={handleClearFilters}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        ) : (
          filteredEvents.map((event, index) => {
            const shouldShowAd = AD_CONFIG.enabled && 
                               AD_CONFIG.placements.inFeed.enabled && 
                               index > 0 && 
                               (index + 1) % AD_CONFIG.placements.inFeed.frequency === 0;
            
            const blogInfo = getBlogForEvent(event['Event Name']);
            const hasBlog = blogInfo !== null;
            const eventTags = getEventDisplayTags(event);
            const hideExternalSiteLink = true;
            const typeClass = getEventTypeClass(event, danceTypeFilter);
            const snapshot = buildSnapshotFromEvent(event);
            const favored = isFavoriteKey(snapshot.key);
            
            return (
              <React.Fragment key={index}>
                <div className={`event-card event-card--${typeClass}`}>
                  <div className="event-card__inner">
                    {/* Header Row */}
                    <div className="event-card__header">
                      <div className="event-card__title-row">
                        <div className="event-card__title-container">
                          <h3 className="event-card__title">{getEventSearchTitle(event)}</h3>
                          {hasBlog && (
                            <svg className="event-card__featured-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z" fill="#FE9A00"/>
                            </svg>
                          )}
                        </div>
                        {eventTags.length > 0 && (
                          <div className="event-card__tags" aria-label={`Event type: ${eventTags.join(', ')}`}>
                            {eventTags.map((label) => {
                              // Pills are always colored by their own label:
                              // Salsa = orange, Bachata = pink (even on filtered views).
                              const pillClass = tagLabelToClassSuffix(label);
                              return (
                                <span
                                  key={label}
                                  className={`event-card__tag event-card__tag--${pillClass || typeClass}`}
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="event-card__header-actions">
                        <button 
                          className={`event-card__favorite ${favored ? 'event-card__favorite--active' : ''}`}
                          onClick={() => {
                            const wasFav = isFavoriteKey(snapshot.key);
                            toggleFavoriteForSnapshot(snapshot);
                            const nowFav = !wasFav;
                            setFavoritesCount(getFavoritesCount());
                            const paramsBase = {
                              event_key: snapshot.key,
                              source: snapshot.source,
                              id_strategy: getIdStrategy(),
                              start_date: snapshot.date || null,
                              start_time: snapshot.timeText || null,
                              dataset_date: dayjs().format('YYYY-MM-DD'),
                              ui_location: 'card',
                              time_parsed: Boolean(snapshot.startDateTimeISO),
                              location_present: Boolean(snapshot.location)
                            };
                            if (nowFav) {
                              trackFavoritesAdd(paramsBase);
                            } else {
                              trackFavoritesRemove(paramsBase);
                            }
                            if (favoritesOnly) {
                              setFilteredEvents(prev => prev.filter(ev => isFavoriteKey(getEventKeyFromEvent(ev))));
                            }
                          }}
                          aria-label={favored ? 'Remove from favorites' : 'Add to favorites'}
                          aria-pressed={favored}
                        >
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 17.5L8.55 16.18C4.4 12.51 1.67 10.01 1.67 6.91C1.67 4.41 3.6 2.5 6.08 2.5C7.5 2.5 8.87 3.16 10 4.24C11.13 3.16 12.5 2.5 13.92 2.5C16.4 2.5 18.33 4.41 18.33 6.91C18.33 10.01 15.6 12.51 11.45 16.18L10 17.5Z" 
                              stroke={favored ? '#FF2056' : '#FB2C36'}
                              strokeWidth="1.66667"
                              fill={favored ? '#FF2056' : 'none'}/>
                          </svg>
                          <span className="event-card__favorite-label">
                            {favored ? 'Favorited' : 'Favorite'}
                          </span>
                        </button>

                        <button
                          type="button"
                          className="event-card__share"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareEvent(event);
                          }}
                          aria-label="Share this event"
                        >
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M11.3333 5.33333C12.4379 5.33333 13.3333 4.4379 13.3333 3.33333C13.3333 2.22876 12.4379 1.33333 11.3333 1.33333C10.2288 1.33333 9.33333 2.22876 9.33333 3.33333C9.33333 4.4379 10.2288 5.33333 11.3333 5.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M4.66667 9.33333C5.77124 9.33333 6.66667 8.4379 6.66667 7.33333C6.66667 6.22876 5.77124 5.33333 4.66667 5.33333C3.5621 5.33333 2.66667 6.22876 2.66667 7.33333C2.66667 8.4379 3.5621 9.33333 4.66667 9.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M11.3333 14.6667C12.4379 14.6667 13.3333 13.7712 13.3333 12.6667C13.3333 11.5621 12.4379 10.6667 11.3333 10.6667C10.2288 10.6667 9.33333 11.5621 9.33333 12.6667C9.33333 13.7712 10.2288 14.6667 11.3333 14.6667Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6.43333 8.22667L9.56667 10.44" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9.56667 5.56L6.43333 7.77333" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Info Bar */}
                    <div className="event-card__info">
                      <div className="event-card__info-item">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="2" y="4" width="16" height="14" rx="2" stroke="#62748E" strokeWidth="1.5"/>
                          <path d="M2 8H18" stroke="#62748E" strokeWidth="1.5"/>
                          <path d="M6 2V4" stroke="#62748E" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M14 2V4" stroke="#62748E" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>{event.Date}</span>
                      </div>
                      <span className="event-card__info-separator">|</span>
                      <div className="event-card__info-item">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="8" stroke="#62748E" strokeWidth="1.5"/>
                          <path d="M10 5V10L13 13" stroke="#62748E" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>{event.Time}</span>
                      </div>
                      <span className="event-card__info-separator">|</span>
                      <div className="event-card__info-item">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 18C10 18 17 12 17 7C17 3.13401 13.866 0 10 0C6.13401 0 3 3.13401 3 7C3 12 10 18 10 18Z" stroke="#62748E" strokeWidth="1.5"/>
                          <circle cx="10" cy="7" r="2.5" stroke="#62748E" strokeWidth="1.5"/>
                        </svg>
                        {(() => {
                          const loc = resolveEventLocation(event);
                          if (loc.isTbd) {
                            return <span>Location: TBD</span>;
                          }
                          return (
                            <a
                              href={`https://maps.google.com/?q=${encodeURIComponent(loc.mapsQuery)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="event-card__info-link"
                            >
                              {loc.cardPrimary}
                              {loc.showSecondary && (
                                <span className="event-card__info-address"> • {loc.cardSecondary}</span>
                              )}
                            </a>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Card summary is generated centrally during event consolidation. */}
                    {typeof event.Summary === 'string' && event.Summary.trim() && (
                      <p className="event-card__description">
                        {event.Summary.trim()}
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div className="event-card__actions">
                      <div className="event-card__actions-left">
                        <button 
                          className={`event-card__btn event-card__btn--primary event-card__btn--${typeClass}`}
                          onClick={() => {
                            const shareId = getShareIdForEvent(event);
                            trackEventDetailsClick(event, {
                              ui_location: 'event_card',
                              destination: `/app/event/${shareId}`,
                            });
                            const params = new URLSearchParams();
                            if (danceTypeFilter && danceTypeFilter !== 'All') {
                              params.append('danceType', danceTypeFilter);
                            }
                            if (locationFilter) {
                              params.append('location', locationFilter);
                            }
                            if (dateFilter) params.append('dateStart', dateFilter.format('YYYY-MM-DD'));
                            if (dateEndFilter) params.append('dateEnd', dateEndFilter.format('YYYY-MM-DD'));
                            const queryString = params.toString();
                            const url = queryString
                              ? `/event/${encodeURIComponent(shareId)}?${queryString}`
                              : `/event/${encodeURIComponent(shareId)}`;
                            // Preserve current filters when returning from EventDetail.
                            // We rely on query params because the list reads them on mount.
                            const from = routerLocation.pathname + (queryString ? `?${queryString}` : routerLocation.search);
                            navigate(url, { state: { from, activeDanceType: danceTypeFilter } });
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M8 5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <circle cx="8" cy="11" r="0.75" fill="currentColor"/>
                          </svg>
                          Details
                        </button>

                        {event.Details && !hideExternalSiteLink && (
                          <a 
                            href={event.Details}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="event-card__btn event-card__btn--secondary"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 8.67V12.67C12 13.02 11.86 13.36 11.61 13.61C11.36 13.86 11.02 14 10.67 14H3.33C2.98 14 2.64 13.86 2.39 13.61C2.14 13.36 2 13.02 2 12.67V5.33C2 4.98 2.14 4.64 2.39 4.39C2.64 4.14 2.98 4 3.33 4H7.33" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 2H14V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M6.67 9.33L14 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Go to Site
                          </a>
                        )}
                      </div>

                      {hasBlog && (
                        <button
                          className="event-card__btn event-card__btn--review"
                          onClick={() => {
                            const params = new URLSearchParams();
                            if (danceTypeFilter && danceTypeFilter !== 'All') {
                              params.append('danceType', danceTypeFilter);
                            }
                            if (locationFilter) {
                              params.append('location', locationFilter);
                            }
                            if (dateFilter) params.append('dateStart', dateFilter.format('YYYY-MM-DD'));
                            if (dateEndFilter) params.append('dateEnd', dateEndFilter.format('YYYY-MM-DD'));
                            const queryString = params.toString();
                            const url = queryString ? `${blogInfo.blogUrl}?${queryString}` : blogInfo.blogUrl;
                            window.location.href = url;
                          }}
                          aria-label={blogInfo.blogTitle}
                        >
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z" fill="white"/>
                          </svg>
                          Our Review
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show ad after every nth event */}
                {shouldShowAd && <InFeedAd />}
              </React.Fragment>
            );
          })
        )}
      </div>


      {/* Back to Top Button */}
      {showBackToTop && (
        <button 
          className="events-back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 19L12 5M12 5L5 12M12 5L19 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        </button>
      )}
    </div>
  );
}

export default EventList;
